import { Button, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SachkostenUmbenennenDialogProps {
  opened: boolean;
  /** Konto-Code aus dem Sachkosten-Katalog — zur Orientierung angezeigt,
   *  nicht editierbar (die Umbenennung wirkt auf dieses eine Konto). */
  kontoCode: string;
  aktuell: string;
  /** Speichert `bezeichnung_override` nur auf der aktuellen Plan-Position. */
  onNurPlan: (bezeichnung: string) => void;
  /** Schreibt die trägerweite Umbenennung (Ebene 2 der Auflösung). */
  onDauerhaft: (bezeichnung: string) => void;
  onClose: () => void;
}

/**
 * Zwei Schaltflächen statt einer Auswahlliste, damit die Entscheidung
 * zwischen "nur dieser Plan" und "dauerhaft" sichtbar ist, statt in einem
 * Dropdown zu verschwinden. Der Hinweis, was "dauerhaft" bedeutet, steht
 * direkt unter dem Knopf — nicht in einem Tooltip, den man übersehen kann.
 */
export function SachkostenUmbenennenDialog({
  opened,
  kontoCode,
  aktuell,
  onNurPlan,
  onDauerhaft,
  onClose,
}: SachkostenUmbenennenDialogProps) {
  const { t } = useTranslation(['sachkosten', 'common']);
  const [wert, setWert] = useState(aktuell);

  useEffect(() => {
    if (opened) setWert(aktuell);
  }, [opened, aktuell]);

  const bezeichnung = wert.trim();
  const disabled = bezeichnung.length === 0;

  return (
    <Modal opened={opened} onClose={onClose} title={t('sachkosten:umbenennen.title')} centered>
      <Stack>
        <Text size="xs" c="dimmed">
          {kontoCode}
        </Text>
        <TextInput
          label={t('sachkosten:umbenennen.feld')}
          value={wert}
          onChange={(e) => setWert(e.currentTarget.value)}
          data-autofocus
        />
        <Button variant="default" fullWidth disabled={disabled} onClick={() => onNurPlan(bezeichnung)}>
          {t('sachkosten:umbenennen.nurPlan')}
        </Button>
        <Stack gap={4}>
          <Button fullWidth disabled={disabled} onClick={() => onDauerhaft(bezeichnung)}>
            {t('sachkosten:umbenennen.dauerhaft')}
          </Button>
          <Text size="xs" c="dimmed">
            {t('sachkosten:umbenennen.dauerhaftHint')}
          </Text>
        </Stack>
        <Button variant="subtle" color="gray" onClick={onClose}>
          {t('common:actions.cancel')}
        </Button>
      </Stack>
    </Modal>
  );
}
